// Lane Pass/Fail + DQR — Underwriting Section 5 (A1) and analyst-tab
// Section 3 (A2/A3/A4).
//
// Lane-agnostic: the analyst tabs reuse this component for A2/A3/A4 by
// passing a different lane prop.
//
// Client-side gate (also enforced server-side):
//   - All checklist items for the lane must be terminal
//     (Approved / Declined / Waived).
//   - Outcome (Pass / Fail) AND DQR (1-5) must be populated.
//   - If Fail, failureReason must be non-empty.

import { useEffect, useState } from "react";
import { Button, Card, Textarea, FormField } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  useCriteriaChecklistByLane,
  useIssueLaneAssessment,
  useLaneAssessment,
} from "@/hooks/dealFlow/useDealFlow";
import { useUndoLaneAssessment } from "@/hooks/dealFlow/useAdminUndo";
import { AdminUndoButton } from "../../admin/AdminUndoButton";
import { isAllItemsTerminal } from "../../CriteriaChecklist";
import { useDateFormatter } from "@/utils/datetime";
import type {
  CriteriaLane,
  DealQualityRating,
  LaneOutcome,
} from "@/api/dealFlow/types";

interface LanePassFailSectionProps {
  dealId: string;
  /** Defaults to A1 — Section 5 of the Underwriting tab is the A1 surface. */
  lane?: CriteriaLane;
  /** Additional gate (e.g. "all Section 2 certs resolved") that
   *  must be true on top of all-items-terminal. */
  additionalGate?: { met: boolean; pendingLabel: string };
  /** When true, hides the form entirely. Issued summary still
   *  renders if a LaneAssessment exists. */
  readOnly?: boolean;
}

const DQR_OPTIONS: ReadonlyArray<{
  value: DealQualityRating;
  label: string;
  hint: string;
}> = [
  { value: "Excellent", label: "1 — Excellent", hint: "Outperforms peers." },
  { value: "Good", label: "2 — Good", hint: "Solid, minor friction." },
  {
    value: "Satisfactory",
    label: "3 — Satisfactory",
    hint: "Median deal, typical risk profile.",
  },
  {
    value: "Fair",
    label: "4 — Fair",
    hint: "Notable concerns; tighter monitoring warranted.",
  },
  {
    value: "Poor",
    label: "5 — Poor",
    hint: "Major issues; consider rejecting outright.",
  },
];

export function LanePassFailSection({
  dealId,
  lane = "A1",
  additionalGate,
  readOnly = false,
}: LanePassFailSectionProps) {
  const items = useCriteriaChecklistByLane(dealId, lane);
  const existing = useLaneAssessment(dealId, lane);
  const issue = useIssueLaneAssessment(dealId);
  const undoIssue = useUndoLaneAssessment(dealId, lane);
  const toast = useToast();
  const { dateTime } = useDateFormatter();

  const [outcome, setOutcome] = useState<LaneOutcome | null>(null);
  const [dqr, setDqr] = useState<DealQualityRating | null>(null);
  const [failureReason, setFailureReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // If a lane assessment already exists, lock the section.
  useEffect(() => {
    if (existing.data) {
      setOutcome(existing.data.outcomePassFail);
      setDqr(existing.data.dealQualityRating);
      setFailureReason(existing.data.failureReason ?? "");
    }
  }, [existing.data]);

  const allTerminal = isAllItemsTerminal(items.data ?? []);
  const additionalGateMet = additionalGate ? additionalGate.met : true;
  const issued = !!existing.data;
  const isValid =
    !!outcome && !!dqr && (outcome === "Pass" || failureReason.trim().length > 0);

  const onIssue = async () => {
    if (!outcome || !dqr) return;
    try {
      await issue.mutateAsync({
        lane,
        body: {
          outcomePassFail: outcome,
          dealQualityRating: dqr,
          failureReason: outcome === "Fail" ? failureReason : null,
        },
      });
      toast.success(
        `${lane} lane ${outcome} issued. DQR: ${dqr}.`,
      );
      setConfirmOpen(false);
    } catch (err) {
      // 409 = the lane was already issued (typically a stale-UI double
      // submit). Treat as soft-success: refetch so the section flips to
      // ISSUED, close the modal, and surface a calm confirmation rather
      // than the hostile "already exists" error.
      if (err instanceof ApiError && err.status === 409) {
        await existing.refetch();
        setConfirmOpen(false);
        toast.success("This lane was already issued.");
        return;
      }
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not issue lane assessment.",
      );
    }
  };

  return (
    <Card>
      <header className="mb-space-3">
        <h3 className="font-display text-h4 text-onyx m-0">
          Section {lane === "A1" ? 5 : 3} — Lane Pass / Fail ({lane})
        </h3>
        <p className="text-caption text-gray-mid m-0">
          Issue your final Pass/Fail call and DQR (1–5). All checklist items
          must reach a terminal state first.
        </p>
      </header>

      {issued && existing.data && (
        <div className="rounded-md border border-status-success/30 bg-status-success/10 px-space-3 py-space-2 mb-space-3">
          <p className="text-small text-onyx m-0 flex items-center gap-space-2">
            <span>
              <strong>{lane} issued {existing.data.outcomePassFail}</strong> · DQR{" "}
              {existing.data.dealQualityRating} · {" "}
              {dateTime(existing.data.assessedAt)}
            </span>
            <AdminUndoButton
              label="Issuance"
              modalTitle={`Undo ${lane} Lane Assessment Issuance`}
              modalConsequence={
                <>
                  This soft-deletes the lane assessment, returning the lane
                  to Ready state. If the Deal Outcome has been finalized,
                  the server will refuse this undo until the finalize is
                  undone first. Re-issuance creates a new row; the soft-
                  deleted row stays as historical record.
                </>
              }
              successToast={`${lane} lane assessment undone.`}
              invoke={(reason) => undoIssue.mutateAsync(reason)}
            />
          </p>
          {existing.data.failureReason && (
            <p className="mt-space-1 text-caption text-status-error m-0">
              Reason: {existing.data.failureReason}
            </p>
          )}
        </div>
      )}

      {readOnly && !issued && (
        <p className="text-small text-gray-mid italic m-0">
          Lane assessment will appear here once {lane} issues their Pass/Fail.
        </p>
      )}

      {!readOnly && (
      <>
      <fieldset className="mb-space-3 m-0 p-0 border-0" disabled={issued}>
        <legend className="font-display text-label uppercase tracking-label text-gold-shadow mb-space-2">
          Outcome
        </legend>
        <div className="flex gap-space-3">
          <RadioRow
            name={`outcome-${lane}`}
            checked={outcome === "Pass"}
            onChange={() => setOutcome("Pass")}
            label="Pass"
            disabled={issued}
          />
          <RadioRow
            name={`outcome-${lane}`}
            checked={outcome === "Fail"}
            onChange={() => setOutcome("Fail")}
            label="Fail"
            disabled={issued}
          />
        </div>
      </fieldset>

      <fieldset className="mb-space-3 m-0 p-0 border-0" disabled={issued}>
        <legend className="font-display text-label uppercase tracking-label text-gold-shadow mb-space-2">
          Deal Quality Rating
        </legend>
        <div className="flex flex-col gap-space-2">
          {DQR_OPTIONS.map((opt) => (
            <RadioRow
              key={opt.value}
              name={`dqr-${lane}`}
              checked={dqr === opt.value}
              onChange={() => setDqr(opt.value)}
              label={`${opt.label} — ${opt.hint}`}
              disabled={issued}
            />
          ))}
        </div>
      </fieldset>

      {outcome === "Fail" && !issued && (
        <FormField label="Failure reason" required>
          <Textarea
            rows={4}
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value)}
            placeholder="Summarize the gating concern(s) that drive the Fail call."
          />
        </FormField>
      )}

      {!issued && (
        <div className="mt-space-3 flex items-center justify-between gap-space-3">
          <span className="text-caption text-gray-mid">
            {!allTerminal
              ? "Awaiting terminal state on all checklist items."
              : !additionalGateMet
                ? additionalGate?.pendingLabel ?? "Awaiting additional gates."
                : "Ready to issue."}
          </span>
          <Button
            variant="primary"
            onClick={() => setConfirmOpen(true)}
            disabled={
              !isValid || !allTerminal || !additionalGateMet || issue.isPending
            }
            title={
              !allTerminal
                ? "Every checklist item must be Approved, Declined, or Waived."
                : !additionalGateMet
                  ? additionalGate?.pendingLabel
                  : undefined
            }
          >
            {issue.isPending
              ? "Issuing…"
              : `Issue ${lane} ${outcome ?? "outcome"}`}
          </Button>
        </div>
      )}
      </>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => !issue.isPending && setConfirmOpen(false)}
        title={`Issue ${outcome ?? ""} for ${lane} lane?`}
        description="This action is final and cannot be reversed except by an Admin override. The lane assessment is written to the audit log immediately."
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={issue.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void onIssue()}
              disabled={issue.isPending}
            >
              {issue.isPending ? "Issuing…" : `Confirm: Issue ${outcome ?? ""}`}
            </Button>
          </>
        }
      >
        <p className="text-small text-onyx m-0">
          You are about to issue <strong>{outcome}</strong> for the{" "}
          <strong>{lane}</strong> lane with DQR <strong>{dqr}</strong>.
        </p>
        {outcome === "Fail" && (
          <p className="mt-space-2 text-caption text-status-error m-0">
            Failure reason: {failureReason}
          </p>
        )}
        <p className="mt-space-3 text-caption text-gray-mid m-0">
          {lane !== "A1" &&
            `Once issued, A1's Section 6 verification slot for ${lane} becomes available. `}
          This action is permanent.
        </p>
      </Modal>
    </Card>
  );
}

function RadioRow({
  name,
  checked,
  onChange,
  label,
  disabled,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-space-2 text-small ${
        disabled ? "opacity-70" : "cursor-pointer"
      }`}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-[2px]"
      />
      <span>{label}</span>
    </label>
  );
}
